import React, { useCallback, useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { X } from "react-feather"
import { connect } from "react-redux"

import API from "api"
import { mapDispatchProps, mapStateToProps } from "store/store"
import Button from "components/button"
import Checkbox from "components/checkbox"
import MatchRatingsEditor, { MATCH_RATING_EDITOR_MODES } from "components/matchRatingsEditor"
import { APP_LOCALE, useLanguage } from "@languages/index"

import "./styles.scss"

interface Props {
  [key:string]:any,
  hasCloseButton?: boolean,
  shouldShowCautionModal?: boolean,
  onCompleteSubmit?: (styles, categories) => void,
}

const OverlayMatchRatingSetting = (props: any) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.component.overlayMatchRatingSetting }

  const stylesRef = useRef([]);
  const categoriesRef = useRef([]);

  const [isHidden, setIsHidden] = useState(true)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmittingState] = useState(isSubmittingRef.current);
  const [isMatchRatingsEditorLoaded, setIsMatchRatingsEditorLoaded] = useState(false);



  /********************************************************************
   **  STATE FUNCTIONS
   ********************************************************************/
  const setIsSubmitting = (newValue:boolean) => {
    isSubmittingRef.current = newValue;
    setIsSubmittingState(newValue);
  }



  /********************************************************************
   **  useEffects
   ********************************************************************/

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, []);



  /********************************************************************
   **  REQUESTS
   ********************************************************************/

  const submit = async () => {
    if (isSubmittingRef.current) {
      props.showModal({
        text: languageTexts.is_requesting,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await API.MatchRating.bulkStoreOrUpdateRatings({
        brandToken: props.auth.token,
        styles: stylesRef.current,
        categories: categoriesRef.current,
      });

      if (props.onCompleteSubmit) {
        props.onCompleteSubmit(stylesRef.current, categoriesRef.current);
      }

      props.showModal({
        text: languageTexts.index_text_4,
        confirm: () => {
          setIsSubmitting(false);
          closeOverlays();
        }
      })
    } catch (error) {
      props.showModal({
        text: error.message,
      });
      setIsSubmitting(false);
    }
  }



  /********************************************************************
   **  HELPER FUNCTIONS
   ********************************************************************/

  const closeOverlays = () => {
    if (props.preferences.isMobile) {
      props.closeSheet()
    } else {
      props.closePopup()
    }
  }


  
  /********************************************************************
   **  EVENT HANDLERS
   ********************************************************************/



  /********************************************************************
   **  RENDERERS
   ********************************************************************/

  const CautionModal = (componentProps:{
    [key:string]:any,
    onConfirm:() => void,
    onCancel:() => void,
  }) => {
    const [doNotShowAgain, setDoNotShowAgain] = useState(false);

    return (
      <div className="flex flex-col justify-center align-center p-8 bg-white">
        <div className="content flex flex-col">
          <span className="font-bold text-[22px] text-black">
            {languageTexts.index_text_5}
          </span>
        </div>
        <div className="mt-4 text-[14px] text-black whitespace-pre-wrap">
          {languageTexts.index_text_6}
        </div>
        <div className="mt-7 flex flex-row gap-[10px]">
          <Checkbox
            checked={doNotShowAgain}
            text={languageTexts.index_text_7}
            onClick={() => {
              setDoNotShowAgain(!doNotShowAgain);
            }}
          />
        </div>
        <div className="mt-6 buttons flex gap-1.5">
          <Button
            className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
            borderColor={"BLACK"}
            text={languageTexts.cancel}
            onClick={() => {
              componentProps.onCancel();
            }}
          />
          <Button
            className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
            borderColor={"BLACK"}
            buttonColor={"BLACK"}
            textColor={"WHITE"}
            text={languageTexts.confirm}
            onClick={() => {
              if (doNotShowAgain) {
                props.setPreferences({
                  isMatchRatingSettingCautionModalDisabled: true,
                })
              }
              componentProps.onConfirm();
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={classNames([
        "relative bg-white rounded-lg shadow-md",
        isMatchRatingsEditorLoaded ? "py-[50px]" : ""
      ])}
      style={{ width: windowWidth < 480 ? `${windowWidth}px` : "480px" }}
    >
      {props.hasCloseButton && (
        <div
          className="absolute right-0 top-0 p-[20px] cursor-pointer"
          onClick={() => {
            props.closeOverlays()
          }}
        >
          <X strokeWidth={1} />
        </div>
      )}
      
      <div className={classNames([
        "max-h-[80dvh]",
        isMatchRatingsEditorLoaded ? "mx-[50px] overflow-y-auto scrollbar-visable" : ""
      ])}>
        <MatchRatingsEditor
          {...props}
          containerClassName={isMatchRatingsEditorLoaded ? "" : ""}
          mode={MATCH_RATING_EDITOR_MODES.OVERLAY_MATCH_RATING_SETTINGS}
          onChangeStyles={(newStyles) => {
            stylesRef.current = newStyles;
          }}
          onChangeCategories={(newCategories) => {
            categoriesRef.current = newCategories;
          }}
          onLoaded={(isLoaded) => {
            setIsMatchRatingsEditorLoaded(isLoaded);
          }}
        />
      </div>

      {isMatchRatingsEditorLoaded && (
        <div className="mt-[50px] px-[50px] w-full flex flex-col gap-[15px]">
          <Button
            text={languageTexts.done}
            buttonColor={isSubmitting ? "#d8d8d8" : "black"}
            textColor={"white"}
            onClick={() => {
              if (
                props.preferences.isMatchRatingSettingCautionModalDisabled != true &&
                props.shouldShowCautionModal === true 
              ) {
                props.showModal({
                  hideCloseButton: true,
                  content: () => {
                    return (
                      <CautionModal
                        {...props}
                        onCancel={() => {
                          props.closeModal();
                        }}
                        onConfirm={() => {
                          submit();
                        }}
                      />
                    );
                  },
                })
              } else {
                submit();
              }
            }}
          />
        </div>
      )}
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(OverlayMatchRatingSetting)
